import { useState, useEffect, useCallback } from 'react';

/**
 * EIP-6963 Provider Info
 * https://eips.ethereum.org/EIPS/eip-6963
 */
export interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string; // Data URI
  rdns: string; // Reverse DNS identifier
}

export interface EIP6963ProviderDetail {
  info: EIP6963ProviderInfo;
  provider: EIP1193Provider;
}

export interface EIP1193Provider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
}

interface EIP6963AnnounceProviderEvent extends Event {
  detail: EIP6963ProviderDetail;
}

// Known wallet identifiers for fallback detection
const KNOWN_WALLETS: Record<string, { name: string; icon: string; rdns: string }> = {
  isMetaMask: {
    name: 'MetaMask',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 318.6 318.6"><path fill="%23E2761B" d="M274.1 35.5l-99.5 73.9L193 65.8z"/><path fill="%23E4761B" d="M44.4 35.5l98.7 74.6-17.5-44.3zm193.9 171.3l-26.5 40.6 56.7 15.6 16.3-55.3zm-204.4.9L50.1 263l56.7-15.6-26.5-40.6z"/><path fill="%23E4761B" d="M103.6 138.2l-15.8 23.9 56.3 2.5-2-60.5zm111.3 0l-39-34.8-1.3 61.2 56.2-2.5zM106.8 247.4l33.8-16.5-29.2-22.8zm71.1-16.5l33.9 16.5-4.7-39.3z"/><path fill="%23D7C1B3" d="M211.8 247.4l-33.9-16.5 2.7 22.1-.3 9.3zm-105 0l31.5 14.9-.2-9.3 2.5-22.1z"/><path fill="%23233447" d="M138.8 193.5l-28.2-8.3 19.9-9.1zm40.9 0l8.3-17.4 20 9.1z"/><path fill="%23CD6116" d="M106.8 247.4l4.8-40.6-31.3.9zM207 206.8l4.8 40.6 26.5-39.7zm23.8-44.7l-56.2 2.5 5.2 28.9 8.3-17.4 20 9.1zm-120.2 23.1l20-9.1 8.2 17.4 5.3-28.9-56.3-2.5z"/><path fill="%23E4751F" d="M87.8 162.1l23.6 46-.8-22.9zm120.3 23.1l-1 22.9 23.7-46zm-64-20.6l-5.3 28.9 6.6 34.1 1.5-44.9zm30.5 0l-2.7 18 1.2 45 6.7-34.1z"/><path fill="%23F6851B" d="M179.8 193.5l-6.7 34.1 4.8 3.3 29.2-22.8 1-22.9zm-69.2-8.3l.8 22.9 29.2 22.8 4.8-3.3-6.6-34.1z"/><path fill="%23C0AD9E" d="M180.3 262.3l.3-9.3-2.5-2.2h-37.7l-2.3 2.2.2 9.3-31.5-14.9 11 9 22.3 15.5h38.3l22.4-15.5 11-9z"/><path fill="%23161616" d="M177.9 230.9l-4.8-3.3h-27.7l-4.8 3.3-2.5 22.1 2.3-2.2h37.7l2.5 2.2z"/><path fill="%23763D16" d="M278.3 114.2l8.5-40.8-12.7-37.9-96.2 71.4 37 31.3 52.3 15.3 11.6-13.5-5-3.6 8-7.3-6.2-4.8 8-6.1zM31.8 73.4l8.5 40.8-5.4 4 8 6.1-6.1 4.8 8 7.3-5 3.6 11.5 13.5 52.3-15.3 37-31.3-96.2-71.4z"/><path fill="%23F6851B" d="M267.2 153.5l-52.3-15.3 15.9 23.9-23.7 46 31.2-.4h46.5zm-163.6-15.3l-52.3 15.3-17.4 54.2h46.4l31.1.4-23.6-46zm71 26.4l3.3-57.7 15.2-41.1h-67.5l15 41.1 3.5 57.7 1.2 18.2.1 44.8h27.7l.2-44.8z"/></svg>',
    rdns: 'io.metamask',
  },
  isCoinbaseWallet: {
    name: 'Coinbase Wallet',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><rect fill="%230052FF" width="1024" height="1024" rx="200"/><path fill="white" d="M512 192c-176.7 0-320 143.3-320 320s143.3 320 320 320 320-143.3 320-320-143.3-320-320-320zm0 480c-88.4 0-160-71.6-160-160s71.6-160 160-160 160 71.6 160 160-71.6 160-160 160z"/></svg>',
    rdns: 'com.coinbase.wallet',
  },
  isTrust: {
    name: 'Trust Wallet',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><rect fill="%230500FF" width="1024" height="1024" rx="200"/><path fill="white" d="M512 180c-183 0-332 149-332 332 0 142.7 90.3 264.3 217 311.1V512c0-63.5 51.5-115 115-115s115 51.5 115 115v311.1c126.7-46.8 217-168.4 217-311.1 0-183-149-332-332-332z"/></svg>',
    rdns: 'com.trustwallet.app',
  },
  isRabby: {
    name: 'Rabby',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><rect fill="%238697FF" width="1024" height="1024" rx="200"/><ellipse cx="512" cy="512" rx="300" ry="280" fill="white"/></svg>',
    rdns: 'io.rabby',
  },
  isPhantom: {
    name: 'Phantom',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><rect fill="%23AB9FF2" width="1024" height="1024" rx="200"/><path fill="white" d="M512 200c-172.5 0-312 139.5-312 312s139.5 312 312 312 312-139.5 312-312-139.5-312-312-312z"/></svg>',
    rdns: 'app.phantom',
  },
  isOKExWallet: {
    name: 'OKX Wallet',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><rect fill="black" width="1024" height="1024" rx="200"/><rect x="200" y="200" width="250" height="250" fill="white"/><rect x="575" y="200" width="250" height="250" fill="white"/><rect x="200" y="575" width="250" height="250" fill="white"/><rect x="387" y="387" width="250" height="250" fill="white"/></svg>',
    rdns: 'com.okex.wallet',
  },
  isBraveWallet: {
    name: 'Brave Wallet',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><rect fill="%23FB542B" width="1024" height="1024" rx="200"/><path fill="white" d="M512 150L200 350v324l312 200 312-200V350z"/></svg>',
    rdns: 'com.brave.wallet',
  },
  isFrame: {
    name: 'Frame',
    icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><rect fill="%231A1A1A" width="1024" height="1024" rx="200"/><rect x="200" y="200" width="624" height="624" rx="50" stroke="white" stroke-width="40" fill="none"/></svg>',
    rdns: 'sh.frame',
  },
};

/**
 * Hook to discover available wallets using EIP-6963 and fallback detection
 */
export function useWalletDiscovery() {
  const [wallets, setWallets] = useState<EIP6963ProviderDetail[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(true);

  // Handle EIP-6963 provider announcement
  const handleAnnouncement = useCallback((event: Event) => {
    const { detail } = event as EIP6963AnnounceProviderEvent;

    setWallets((prev) => {
      // Avoid duplicates by checking UUID
      if (prev.some((w) => w.info.uuid === detail.info.uuid)) {
        return prev;
      }
      return [...prev, detail];
    });
  }, []);

  // Fallback detection for wallets that don't support EIP-6963
  const detectLegacyWallets = useCallback(() => {
    if (typeof window === 'undefined') return;

    const detected: EIP6963ProviderDetail[] = [];
    const ethereum = window.ethereum as EIP1193Provider & Record<string, boolean> | undefined;

    if (!ethereum) return detected;

    // Check for known wallet flags
    for (const [flag, info] of Object.entries(KNOWN_WALLETS)) {
      if (ethereum[flag]) {
        detected.push({
          info: {
            uuid: `legacy-${info.rdns}`,
            name: info.name,
            icon: info.icon,
            rdns: info.rdns,
          },
          provider: ethereum,
        });
        break; // Only add the primary wallet from window.ethereum
      }
    }

    // If no known wallet detected but ethereum exists, add generic
    if (detected.length === 0 && ethereum) {
      detected.push({
        info: {
          uuid: 'legacy-injected',
          name: 'Browser Wallet',
          icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M22 10H2"/></svg>',
          rdns: 'injected',
        },
        provider: ethereum,
      });
    }

    return detected;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsDiscovering(false);
      return;
    }

    // Listen for EIP-6963 announcements
    window.addEventListener('eip6963:announceProvider', handleAnnouncement);

    // Request wallet announcements
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    // Give wallets time to announce, then fall back to legacy detection
    const timeoutId = setTimeout(() => {
      setWallets((prev) => {
        // If no EIP-6963 wallets found, use legacy detection
        if (prev.length === 0) {
          const legacyWallets = detectLegacyWallets();
          return legacyWallets || [];
        }
        return prev;
      });
      setIsDiscovering(false);
    }, 500);

    return () => {
      window.removeEventListener('eip6963:announceProvider', handleAnnouncement);
      clearTimeout(timeoutId);
    };
  }, [handleAnnouncement, detectLegacyWallets]);

  // Refresh wallet discovery
  const refresh = useCallback(() => {
    setIsDiscovering(true);
    setWallets([]);

    // Re-request announcements
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    setTimeout(() => {
      setWallets((prev) => {
        if (prev.length === 0) {
          const legacyWallets = detectLegacyWallets();
          return legacyWallets || [];
        }
        return prev;
      });
      setIsDiscovering(false);
    }, 500);
  }, [detectLegacyWallets]);

  return {
    wallets,
    isDiscovering,
    refresh,
    hasWallets: wallets.length > 0,
  };
}
